import { useMutation, useQuery, useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import {
  makeMarketingManagerApi,
  IMarketingManagerOpportunityRequest,
  IMarketingManagerOpportunityUpdateRequest,
  IMarketingManagerOpportunitiesResponse,
} from 'services/MarketingManagerApi';
import { makeBackendApi } from 'services/BackendApi';
import { ESortOrder } from 'store/common/types';

export const useContentUploadUrl = () => {
  const marketingManagerApi = makeMarketingManagerApi();

  return useMutation({
    mutationFn: async ({
      contentUuid,
      contentType,
      fileName,
    }: {
      contentUuid: string;
      contentType: string;
      fileName: string;
    }) => {
      const response = await marketingManagerApi.getContentUploadUrl({
        contentUuid,
        contentType,
        fileName,
      });
      return response.data;
    },
  });
};

export const useContentProcessUpload = () => {
  const marketingManagerApi = makeMarketingManagerApi();

  return useMutation({
    mutationFn: async ({
      contentUuid,
      displayName,
      filePath,
      fileType,
    }: {
      contentUuid: string;
      displayName: string;
      filePath: string;
      fileType: 'image' | 'file';
    }) => {
      const response = await marketingManagerApi.postContentProcessUpload({
        contentUuid,
        displayName,
        filePath,
        fileType,
      });
      return response.data;
    },
  });
};

export const useCreateOpportunity = () => {
  const marketingManagerApi = makeMarketingManagerApi();

  return useMutation({
    mutationFn: async (opportunityData: IMarketingManagerOpportunityRequest) => {
      const response = await marketingManagerApi.createOpportunity(opportunityData);
      return response.data;
    },
  });
};

export const useCategories = () => {
  const marketingManagerApi = makeMarketingManagerApi();

  return useQuery({
    queryKey: ['marketing-manager', 'categories'],
    queryFn: async () => {
      const response = await marketingManagerApi.getCategories();
      return response.data;
    },
  });
};

export const useDestinations = () => {
  const backendApi = makeBackendApi();

  return useQuery({
    queryKey: ['backend', 'destinations'],
    queryFn: async () => {
      const response = await backendApi.getNamesSearch('');
      const countries = response.data.data.countries || [];
      return countries.map(country => ({
        name: country.name,
      }));
    },
  });
};

export const useProductTypes = () => {
  const marketingManagerApi = makeMarketingManagerApi();

  return useQuery({
    queryKey: ['marketing-manager', 'product-types'],
    queryFn: async () => {
      const response = await marketingManagerApi.getProductTypes();
      return response.data;
    },
  });
};

export const useOpportunity = (uuid: string | null) => {
  const marketingManagerApi = makeMarketingManagerApi();

  return useQuery({
    queryKey: ['marketing-manager', 'opportunity', uuid],
    queryFn: async () => {
      if (!uuid) throw new Error('UUID is required');
      const response = await marketingManagerApi.getOpportunity(uuid);
      return response.data;
    },
    enabled: !!uuid, // Only run query if UUID is provided
    staleTime: 0, // Always consider data stale, refetch on mount
  });
};

export const useUpdateOpportunity = () => {
  const marketingManagerApi = makeMarketingManagerApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ uuid, opportunityData }: { uuid: string; opportunityData: IMarketingManagerOpportunityUpdateRequest }) => {
      const response = await marketingManagerApi.updateOpportunity(uuid, opportunityData);
      return response.data;
    },
    onSuccess: (data, variables) => {
      // Immediately update the cache with the fresh data from the mutation response
      // This ensures the data is available immediately without waiting for a refetch
      queryClient.setQueryData(['marketing-manager', 'opportunity', variables.uuid], data);
      // Also invalidate the opportunities list to keep it in sync
      queryClient.invalidateQueries({ queryKey: ['marketing-manager', 'opportunities'] });
    },
  });
};

export const useOpportunityList = (
  page: number,
  limit: number,
  sortBy: string,
  order: ESortOrder,
  filters: {
    title?: string;
    crmCompany?: string;
    categoryUuids?: string[];
    destinations?: string[];
    status?: 'Draft' | 'Active' | 'Scheduled' | 'Expired';
    publishDateFrom?: string;
    publishDateTo?: string;
  }
) => {
  const marketingManagerApi = makeMarketingManagerApi();

  return useQuery({
    queryKey: [
      'marketing-manager',
      'opportunities',
      page,
      limit,
      sortBy,
      order,
      filters.title,
      filters.crmCompany,
      filters.categoryUuids,
      filters.destinations,
      filters.status,
      filters.publishDateFrom,
      filters.publishDateTo,
    ],
    queryFn: async () => {
      const response = await marketingManagerApi.getOpportunities({
        page,
        limit,
        sortBy,
        order,
        ...filters,
      });
      return response.data;
    },
  });
};

export const useOpportunitiesInfinite = ({
  pageSize = 6,
  categoryUuids = [],
  destinations = [],
  sortBy = 'creationDate',
  order = ESortOrder.DESC,
}: {
  pageSize?: number;
  categoryUuids?: string[];
  destinations?: string[];
  sortBy?: 'creationDate' | 'title' | 'crmCompany' | 'category' | 'destination' | 'isExclusive' | 'activeFrom' | 'activeTo';
  order?: ESortOrder;
}) => {
  const marketingManagerApi = makeMarketingManagerApi();
  return useInfiniteQuery({
    initialPageParam: 1,
    queryKey: ['marketing-manager', 'opportunities', 'cards', pageSize, categoryUuids, destinations, sortBy, order],
    queryFn: ({ pageParam = 0 }) => {
      return marketingManagerApi.listOpportunitiesCards({
        page: Number(pageParam),
        limit: pageSize,
        sortBy,
        order,
        categoryUuids,
        destinations,
      })
    },
    getNextPageParam: (lastPage) => {
      return lastPage.data.totalPages > lastPage.data.page ? lastPage.data.page + 1 : undefined
    },
  });
};

export const useDeleteOpportunity = () => {
  const marketingManagerApi = makeMarketingManagerApi();

  return useMutation({
    mutationFn: async (uuid: string) => {
      const response = await marketingManagerApi.deleteOpportunity(uuid);
      return response.data;
    },
  });
};